import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {TrailRatingData} from "types/TrailRating";
import TrailRatingForm from "pages/TrailRating/Form";
import {FormikHelpers} from "formik";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {useOperator} from "../../../context/OperatorContext";

function MutateTrailRating() {
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const auth = useAuth();
  const {id} = useParams();
  const {termFor} = useOperator();

  const {data: trailRating, isLoading} = useQuery(
    ['trail-ratings', {id}],
    () => ngage.trailRatings.get(Number(id)),
    {enabled: !!id && auth.can('trail-ratings:read')}
  )

  const store = useMutation((data: TrailRatingData) => ngage.trailRatings.store(data))
  const update = useMutation((data: TrailRatingData) => ngage.trailRatings.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: TrailRatingData, {setSubmitting}: FormikHelpers<TrailRatingData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('trail-ratings.all')

    setSubmitting(false)
    navigate('/trail-ratings')
  }

  // Handle store mutation
  async function handleStore(values: TrailRatingData) {
    try {
      await store.mutateAsync(values)
      notify("success", "Trail Rating Created", `${values.name} has been created`)
    } catch (error) {
      notify("error", "Trail Rating Not Created", `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: TrailRatingData) {
    try {
      await update.mutateAsync(values)
      notify("success", "Trail Rating Updated", `${values.name} has been updated`)
      await invalidate(['trail-ratings', {id}])
    } catch (error) {
      notify("error", "Trail Rating Not Updated", `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised mutation
  if (auth.cannot('trail-ratings:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Trail') + ' Ratings']: '/trail-ratings',
    ['New ' + termFor('Trail') + ' Rating']: '/trail-ratings/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Trail') + ' Ratings']: '/trail-ratings',
    ['Edit ' + termFor('Trail') + ' Rating']: `/trail-ratings/${id}/edit`
  }

  return (
    <Page
      title={id ? `Edit ${termFor('Trail')} Rating` : `New ${termFor('Trail')} Rating`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <TrailRatingForm trailRating={trailRating} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateTrailRating;